'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';
import type { RequestFormData, ServiceCategory, CleaningSubType } from './ServiceRequestWizard';

interface StepOneProps {
  formData: RequestFormData;
  updateFormData: (updates: Partial<RequestFormData>) => void;
  onNext: () => void;
}

const serviceCategories = [
  {
    id: 'cleaning' as ServiceCategory,
    label: 'Cleaning',
    icon: 'SparklesIcon',
    description: 'House, driveway/garage floor, pool or spa',
    color: 'text-blue-600',
    bg: 'bg-blue-50 border-blue-200',
    activeBg: 'bg-blue-100 border-blue-500',
  },
  {
    id: 'gardening' as ServiceCategory,
    label: 'Gardening & Lawn',
    icon: 'SunIcon',
    description: 'Lawn mowing, garden maintenance, hedging, mulching',
    color: 'text-green-600',
    bg: 'bg-green-50 border-green-200',
    activeBg: 'bg-green-100 border-green-500',
  },
  {
    id: 'handyman' as ServiceCategory,
    label: 'Handyman & Odd Jobs',
    icon: 'WrenchScrewdriverIcon',
    description: 'Repairs, installations, painting, general maintenance',
    color: 'text-amber-600',
    bg: 'bg-amber-50 border-amber-200',
    activeBg: 'bg-amber-100 border-amber-500',
  },
];

const cleaningSubTypes = [
  { id: 'house' as CleaningSubType, label: 'House / Unit Cleaning', icon: 'HomeIcon', desc: 'Interior rooms, kitchen, bathrooms' },
  { id: 'driveway' as CleaningSubType, label: 'Driveway / Garage Floor', icon: 'TruckIcon', desc: 'Pressure wash, oil stain removal' },
  { id: 'pool' as CleaningSubType, label: 'Pool / Spa Cleaning', icon: 'BeakerIcon', desc: 'Water treatment, vacuuming, filter clean' },
];

const gardenServiceOptions = [
  'Lawn mowing & edging',
  'Hedge trimming',
  'Garden bed weeding',
  'Mulching',
  'Tree / shrub pruning',
  'Leaf & debris removal',
  'Fertilising',
  'Irrigation check',
];

const handymanCategories = [
  'Carpentry & Timber Work',
  'Painting & Patching',
  'Plumbing (minor)',
  'Electrical (minor)',
  'Tiling',
  'Flat-pack assembly',
  'Fence & gate repair',
  'Gutter cleaning',
  'Pressure washing',
  'General odd jobs',
];

export default function StepOne({ formData, updateFormData, onNext }: StepOneProps) {
  const canProceed =
    formData.serviceCategory !== '' &&
    (formData.serviceCategory !== 'cleaning' || formData.cleaningSubType !== '') &&
    (formData.serviceCategory !== 'cleaning' || validateCleaningFields(formData)) &&
    (formData.serviceCategory !== 'gardening' || (formData.gardenSize && (formData.gardenServices?.length ?? 0) > 0)) &&
    (formData.serviceCategory !== 'handyman' || (formData.jobCategory && formData.jobDescription));

  function validateCleaningFields(data: RequestFormData) {
    if (data.cleaningSubType === 'house') return !!(data.bedrooms && data.bathrooms && data.cleaningType);
    if (data.cleaningSubType === 'driveway') return !!(data.drivewaySurface && data.drivewaySize);
    if (data.cleaningSubType === 'pool') return !!(data.poolType && data.poolSize);
    return false;
  }

  const toggleGardenService = (service: string) => {
    const current = formData.gardenServices ?? [];
    const updated = current.includes(service)
      ? current.filter((s) => s !== service)
      : [...current, service];
    updateFormData({ gardenServices: updated });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">What service do you need?</h2>
        <p className="text-sm text-muted-foreground mt-1">Select the category that best describes your job.</p>
      </div>

      {/* Service category selector */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {serviceCategories.map((cat) => {
          const isActive = formData.serviceCategory === cat.id;
          return (
            <button
              key={`cat-${cat.id}`}
              type="button"
              onClick={() => updateFormData({ serviceCategory: cat.id, cleaningSubType: '', gardenServices: [] })}
              className={`relative flex flex-col items-start gap-2 p-4 rounded-xl border-2 text-left transition-all duration-150 ${
                isActive ? cat.activeBg : `${cat.bg} hover:border-opacity-70`
              }`}
            >
              {isActive && (
                <span className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="CheckIcon" size={11} variant="outline" className="text-primary-foreground" />
                </span>
              )}
              <Icon name={cat.icon as any} size={22} variant="outline" className={cat.color} />
              <div>
                <p className="text-sm font-semibold text-foreground">{cat.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{cat.description}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Cleaning sub-type */}
      {formData.serviceCategory === 'cleaning' && (
        <div className="space-y-4 animate-slide-up">
          <div>
            <p className="text-sm font-semibold text-foreground mb-2">What type of cleaning?</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {cleaningSubTypes.map((sub) => {
                const isActive = formData.cleaningSubType === sub.id;
                return (
                  <button
                    key={`sub-${sub.id}`}
                    type="button"
                    onClick={() => updateFormData({ cleaningSubType: sub.id })}
                    className={`flex flex-col items-start gap-1.5 p-3.5 rounded-xl border-2 text-left transition-all duration-150 ${
                      isActive
                        ? 'bg-secondary border-primary' :'bg-muted/30 border-border hover:border-primary/40'
                    }`}
                  >
                    <Icon name={sub.icon as any} size={18} variant="outline" className={isActive ? 'text-primary' : 'text-muted-foreground'} />
                    <p className={`text-xs font-semibold ${isActive ? 'text-primary' : 'text-foreground'}`}>{sub.label}</p>
                    <p className="text-xs text-muted-foreground leading-tight">{sub.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* House cleaning fields */}
          {formData.cleaningSubType === 'house' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-slide-up">
              <div>
                <label htmlFor="bedrooms" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
                  Bedrooms
                </label>
                <select
                  id="bedrooms"
                  value={formData.bedrooms ?? ''}
                  onChange={(e) => updateFormData({ bedrooms: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select</option>
                  {['1', '2', '3', '4', '5', '6+'].map((n) => (
                    <option key={`bed-${n}`} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="bathrooms" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
                  Bathrooms
                </label>
                <select
                  id="bathrooms"
                  value={formData.bathrooms ?? ''}
                  onChange={(e) => updateFormData({ bathrooms: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select</option>
                  {['1', '2', '3', '4', '5+'].map((n) => (
                    <option key={`bath-${n}`} value={n}>{n}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="cleaningType" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
                  Cleaning Type
                </label>
                <select
                  id="cleaningType"
                  value={formData.cleaningType ?? ''}
                  onChange={(e) => updateFormData({ cleaningType: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select</option>
                  <option value="regular">Regular clean</option>
                  <option value="deep">Deep clean</option>
                  <option value="end-of-lease">End of lease</option>
                  <option value="move-in">Move-in clean</option>
                  <option value="spring">Spring clean</option>
                </select>
              </div>
            </div>
          )}

          {/* Driveway fields */}
          {formData.cleaningSubType === 'driveway' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-slide-up">
              <div>
                <label htmlFor="drivewaySurface" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
                  Surface Type
                </label>
                <select
                  id="drivewaySurface"
                  value={formData.drivewaySurface ?? ''}
                  onChange={(e) => updateFormData({ drivewaySurface: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select surface</option>
                  <option value="concrete">Concrete</option>
                  <option value="pavers">Pavers / brickwork</option>
                  <option value="asphalt">Asphalt</option>
                  <option value="exposed-agg">Exposed aggregate</option>
                  <option value="tiles">Tiles</option>
                </select>
              </div>
              <div>
                <label htmlFor="drivewaySize" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
                  Approximate Size
                </label>
                <select
                  id="drivewaySize"
                  value={formData.drivewaySize ?? ''}
                  onChange={(e) => updateFormData({ drivewaySize: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select size</option>
                  <option value="small">Small (&lt;30m²)</option>
                  <option value="medium">Medium (30–80m²)</option>
                  <option value="large">Large (80–150m²)</option>
                  <option value="extra-large">Extra large (&gt;150m²)</option>
                </select>
              </div>
            </div>
          )}

          {/* Pool fields */}
          {formData.cleaningSubType === 'pool' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-slide-up">
              <div>
                <label htmlFor="poolType" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
                  Pool Type
                </label>
                <select
                  id="poolType"
                  value={formData.poolType ?? ''}
                  onChange={(e) => updateFormData({ poolType: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select type</option>
                  <option value="in-ground">In-ground pool</option>
                  <option value="above-ground">Above-ground pool</option>
                  <option value="spa">Spa only</option>
                  <option value="pool-spa">Pool + spa combo</option>
                  <option value="plunge">Plunge pool</option>
                </select>
              </div>
              <div>
                <label htmlFor="poolSize" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
                  Pool Size
                </label>
                <select
                  id="poolSize"
                  value={formData.poolSize ?? ''}
                  onChange={(e) => updateFormData({ poolSize: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select size</option>
                  <option value="small">Small (&lt;25,000L)</option>
                  <option value="medium">Medium (25,000–50,000L)</option>
                  <option value="large">Large (&gt;50,000L)</option>
                  <option value="spa-only">Spa only</option>
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Gardening fields */}
      {formData.serviceCategory === 'gardening' && (
        <div className="space-y-4 animate-slide-up">
          <div>
            <label htmlFor="gardenSize" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
              Property / Garden Size
            </label>
            <select
              id="gardenSize"
              value={formData.gardenSize ?? ''}
              onChange={(e) => updateFormData({ gardenSize: e.target.value })}
              className="w-full sm:w-64 px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select size</option>
              <option value="small">Small (townhouse / unit)</option>
              <option value="medium">Medium (standard suburban block)</option>
              <option value="large">Large (650m²+)</option>
              <option value="acreage">Acreage</option>
            </select>
          </div>

          <div>
            <p className="text-xs font-semibold text-foreground mb-2 tracking-wide uppercase">Services Required</p>
            <p className="text-xs text-muted-foreground mb-3">Select all that apply</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {gardenServiceOptions.map((service) => {
                const isSelected = formData.gardenServices?.includes(service);
                return (
                  <button
                    key={`garden-svc-${service.replace(/\s/g, '-')}`}
                    type="button"
                    onClick={() => toggleGardenService(service)}
                    className={`text-left px-3 py-2 rounded-lg border text-xs font-medium transition-all duration-150 ${
                      isSelected
                        ? 'bg-secondary border-primary text-primary' :'bg-muted/30 border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                    }`}
                  >
                    {isSelected && '✓ '}{service}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label htmlFor="grassType" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
              Grass Type (if applicable)
            </label>
            <select
              id="grassType"
              value={formData.grassType ?? ''}
              onChange={(e) => updateFormData({ grassType: e.target.value })}
              className="w-full sm:w-64 px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Not sure / N/A</option>
              <option value="buffalo">Buffalo</option>
              <option value="couch">Couch</option>
              <option value="kikuyu">Kikuyu</option>
              <option value="zoysia">Zoysia</option>
              <option value="tall-fescue">Tall Fescue</option>
              <option value="mixed">Mixed / Not sure</option>
            </select>
          </div>
        </div>
      )}

      {/* Handyman fields */}
      {formData.serviceCategory === 'handyman' && (
        <div className="space-y-4 animate-slide-up">
          <div>
            <label htmlFor="jobCategory" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
              Job Category
            </label>
            <select
              id="jobCategory"
              value={formData.jobCategory ?? ''}
              onChange={(e) => updateFormData({ jobCategory: e.target.value })}
              className="w-full sm:w-80 px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select category</option>
              {handymanCategories.map((cat) => (
                <option key={`hcat-${cat.replace(/\s/g, '-')}`} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="jobDescription" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
              Describe the Job
            </label>
            <p className="text-xs text-muted-foreground mb-2">
              The more detail you provide, the more accurate your quote will be.
            </p>
            <textarea
              id="jobDescription"
              value={formData.jobDescription ?? ''}
              onChange={(e) => updateFormData({ jobDescription: e.target.value })}
              rows={4}
              placeholder="e.g. I need two internal doors re-hung — they've dropped and scrape the floor. The hinges look fine, just need adjustment and the strike plates repositioned."
              className="w-full px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          <div>
            <label htmlFor="materialSupply" className="block text-xs font-semibold text-foreground mb-1.5 tracking-wide uppercase">
              Materials
            </label>
            <select
              id="materialSupply"
              value={formData.materialSupply ?? ''}
              onChange={(e) => updateFormData({ materialSupply: e.target.value })}
              className="w-full sm:w-64 px-3 py-2.5 text-sm border border-input rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select</option>
              <option value="client-supply">I'll supply all materials</option>
              <option value="tradesperson-supply">Please supply materials (add to quote)</option>
              <option value="mixed">Mix — I'll supply some, quote rest</option>
              <option value="not-sure">Not sure yet</option>
            </select>
          </div>
        </div>
      )}

      {/* Next button */}
      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground text-sm font-semibold rounded-lg hover:opacity-90 transition-all duration-150 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Continue to Job Details
          <Icon name="ArrowRightIcon" size={16} variant="outline" />
        </button>
      </div>
    </div>
  );
}